import {
  formatDateISOIfDate,
  isDateValid,
  normalizeToLocalDate,
  parseDate,
  startOfDay,
} from '@ardoq/date-time';
import { isRelativeDateValue } from '@ardoq/api-types';
import { omit, pick } from 'lodash';
import type {
  AiTimelineViewSettings,
  PersistedCollapsedState,
  SettableTimelineViewSettings,
  TimelineViewSettings,
} from '@ardoq/timeline2024';

const toAiDomainRange = (
  domainRange: TimelineViewSettings['domainRange']
): SettableTimelineViewSettings['domainRange'] => {
  if (!domainRange) {
    return undefined;
  }
  if (isRelativeDateValue(domainRange)) {
    return domainRange;
  }

  const [start, end] = domainRange;
  return [formatDateISOIfDate(start), formatDateISOIfDate(end)];
};

/** The persisted settings with the visible window narrowed to ISO dates. */
export const toAiTimelineViewSettings = (
  settings: TimelineViewSettings
): SettableTimelineViewSettings => ({
  ...pick(settings, [
    'isLegendActive',
    'selectedMilestoneFields',
    'selectedPhaseFields',
    'gridPrecision',
    'persistedCollapsedState',
  ]),
  domainRange: toAiDomainRange(settings.domainRange),
});

const toStoredDate = (date: string | null): Date | null => {
  if (!date) {
    return null;
  }
  const parsed = parseDate(normalizeToLocalDate(date));
  return isDateValid(parsed) ? startOfDay(parsed) : null;
};

const toStoredDomainRange = (
  domainRange: AiTimelineViewSettings['domainRange']
): Partial<Pick<TimelineViewSettings, 'domainRange'>> => {
  if (domainRange === undefined) {
    return {};
  }
  if (isRelativeDateValue(domainRange)) {
    return { domainRange };
  }

  const [start, end] = domainRange;
  return { domainRange: [toStoredDate(start), toStoredDate(end)] };
};

const toStoredPersistedCollapsedState = (
  settings: AiTimelineViewSettings,
  storedCollapsedState: PersistedCollapsedState | undefined
): Partial<Pick<TimelineViewSettings, 'persistedCollapsedState'>> => {
  const patch = settings.persistedCollapsedState;

  if (patch === undefined) {
    return {};
  }

  return {
    persistedCollapsedState: {
      collapsedGroupIds:
        patch.collapsedGroupIds ??
        storedCollapsedState?.collapsedGroupIds ??
        [],
      collapsedFieldDataIds:
        patch.collapsedFieldDataIds ??
        storedCollapsedState?.collapsedFieldDataIds ??
        [],
    },
  };
};

export const toStoredTimelineViewSettings = (
  settings: AiTimelineViewSettings,
  storedCollapsedState: PersistedCollapsedState | undefined
): Partial<TimelineViewSettings> => ({
  ...omit(settings, ['domainRange', 'persistedCollapsedState']),
  ...toStoredDomainRange(settings.domainRange),
  ...toStoredPersistedCollapsedState(settings, storedCollapsedState),
});
